import { getIdentifiers } from '../../data/helpers';
import { DataStore } from '../../data/store';
import { ChartColors, DataSet } from '../../design/charts';
import {
  ColumnRelationship,
  ColumnType,
  DataColumn,
  DataRow,
  DataRows,
  DataType,
  FormatHint,
  IdentifierColumn,
  KpiWithChangeColumn,
  Params,
  TimeFrame,
} from '../../types';
import { DEFAULT_QUANTITY } from '../constants';
import { ColorHelper, createMarketKpi, formatDataSets, makeDataSet } from '../data/common';
import { formatChartSharedBrands } from '../utils';

export type SegmentDataSet = {
  label: string;
  dataSets: DataSet[];
  totals?: DataSet;
  additionalTotals?: DataSet;
};

export type SegmentedChartData = {
  yLabel: string;
  yType: DataType;
  xLabel: string;
  maxYRange: number;
  segmentDataSets: SegmentDataSet[];
  dataLabels: (string | string[])[];
  totalsLabel?: string;
  totalsChangeLabel?: string;
  additionalTotalsLabel?: string;
  additionalTotalsChangeLabel?: string;
};

const ADDITIONAL_KPI_MAP = (quantityType: string) => {
  const kpiValue = createMarketKpi(quantityType);

  return {
    [kpiValue.revenue]: kpiValue.shareRevenue,
    [kpiValue.quantity]: kpiValue.shareQuantity,
    [kpiValue.shareRevenue]: kpiValue.revenue,
    [kpiValue.shareQuantity]: kpiValue.quantity,
    [kpiValue.priceIndex]: kpiValue.averagePrice,
  };
};

export function makeSegmentedChartData(
  data: DataStore,
  params: Params,
  options: {
    xColumnName: string;
    yColumnName: string;
    segmentName: string;
    sharedBrands?: string[];
  }
): SegmentedChartData | undefined {
  const { xColumnName, yColumnName, segmentName, sharedBrands } = options;
  const columns = data.getDataColumns();

  const xColumn = findColumn(columns, xColumnName);
  const yColumn = findColumn(columns, yColumnName);
  const segmentColumn = findColumn(columns, segmentName);

  if (!xColumn || !yColumn || !segmentColumn) {
    return;
  }

  const withChange = (yColumn as KpiWithChangeColumn)?.changeName !== undefined;
  const quantityType = params?.quantity_type?.[0] ?? DEFAULT_QUANTITY;

  const additionalYColumn = findColumn(columns, ADDITIONAL_KPI_MAP(quantityType)[yColumnName]);

  const xDescColumn = getDescriptionColumn(columns, xColumn, xColumnName);
  const segmentDescColumn = getDescriptionColumn(columns, segmentColumn, segmentName);

  const [totalsData] = data.getGroupedData([]);
  const [xData] = data.getGroupedData([xColumnName]);
  const [segmentData] = data.getGroupedData([segmentName]);
  const [segmentedData] = data.getGroupedData([segmentName, xColumnName]);

  formatSharedBrandsForSegmentation(params, xColumnName, xData, segmentName, segmentData, sharedBrands);

  const colorHelper = new ColorHelper(
    xColumnName,
    xData.data.map(row => String(row.values[xColumnName]))
  );

  const summaryDataSet: SegmentDataSet = {
    label: 'Totals',
    dataSets: xData.data.map((row, index) => {
      const set = makeDataSet(row, {
        xColumn,
        yColumn,
        withChange,
        index,
        colorHelper,
      });
      set.label = getRowLabel(row, xDescColumn);
      return set;
    }),
  };

  const segmentValues: string[] = [];
  const segmentLabels: string[] = [];
  segmentData.data.forEach(row => {
    segmentValues.push(String(row.values[segmentName]));
    segmentLabels.push(getRowLabel(row, segmentDescColumn));
  });

  const segmentDataSets: SegmentDataSet[] = [];
  const segmentSums: number[] = [];
  const segmentMaxValue: number[] = [];
  segmentValues.forEach((segment, sIdx) => {
    const segmentDataSet: SegmentDataSet = {
      label: segmentLabels[sIdx],
      dataSets: [],
    };

    segmentSums.push(0);
    const sums: number[] = [];
    let maxSegmentValue = 0;

    xData.data.forEach((row, rIdx) => {
      const xRowName = String(row.values[xColumnName]);
      const segmentRowForX = segmentedData.data.find(
        r => String(r.values[segmentName]) === segment && String(r.values[xColumnName]) === xRowName
      );

      if (segmentRowForX) {
        const dataSet = makeDataSet(segmentRowForX, {
          xColumn,
          yColumn,
          withChange,
          index: rIdx,
          colorHelper,
        });
        dataSet.label = getRowLabel(row, xDescColumn);
        segmentDataSet.dataSets.push(dataSet);
        dataSet.values.raw.forEach((rawValue, rawIdx) => {
          sums[rawIdx] = sums[rawIdx] ? sums[rawIdx] + rawValue : rawValue;
        });
        maxSegmentValue = Math.max(maxSegmentValue, ...dataSet.values.raw);
      }
    });
    segmentDataSets.push(segmentDataSet);
    segmentSums[sIdx] = Math.max(...sums);
    segmentMaxValue[sIdx] = maxSegmentValue;
  });

  const includeTotals = !!totalsData;

  if (includeTotals) {
    summaryDataSet.totals = makeDataSet(totalsData.data[0], {
      xColumn,
      yColumn,
      index: 0,
      colorHelper,
      withChange,
      bgColor: ChartColors.panelBg,
    });

    if (additionalYColumn) {
      summaryDataSet.additionalTotals = makeDataSet(totalsData.data[0], {
        xColumn,
        yColumn: additionalYColumn,
        index: 0,
        colorHelper,
        withChange,
        bgColor: ChartColors.panelBg,
      });
    }
    segmentValues.forEach((segment, sIdx) => {
      const segmentRow = segmentData.data.find(row => String(row.values[segmentName]) === segment);
      segmentDataSets[sIdx].totals = makeDataSet(segmentRow, {
        xColumn,
        yColumn,
        index: 0,
        colorHelper,
        withChange,
        bgColor: ChartColors.panelBg,
      });

      if (additionalYColumn) {
        segmentDataSets[sIdx].additionalTotals = makeDataSet(segmentRow, {
          xColumn,
          yColumn: additionalYColumn,
          index: 0,
          colorHelper,
          withChange,
          bgColor: ChartColors.panelBg,
        });
      }
    });
  }

  formatSegmentDataSets(segmentDataSets, summaryDataSet, {
    yColumn,
    additionalYColumn,
    currency: data.getCurrency(),
    includeTotals,
  });

  const maxYRange = yColumn.canAggregate ? Math.max(...segmentSums) : Math.max(...segmentMaxValue);

  return {
    yLabel: yColumn.label,
    yType: yColumn.dataType,
    xLabel: xColumn.label,
    segmentDataSets: [summaryDataSet, ...segmentDataSets],
    maxYRange: yColumn.formatHint === FormatHint.Percentage ? 1 : maxYRange,
    dataLabels: xData.timeFrame.periods.map(p => (typeof p.name === 'string' ? p.name.split(' ') : p.name)),
    totalsLabel: includeTotals && yColumn.name !== createMarketKpi().priceIndex ? `Market ${yColumn.label}` : undefined,
    totalsChangeLabel: includeTotals && yColumn.name !== createMarketKpi().priceIndex ? 'Change' : undefined,
    additionalTotalsLabel: includeTotals && additionalYColumn ? `Market ${additionalYColumn.label}` : undefined,
    additionalTotalsChangeLabel: includeTotals && additionalYColumn ? 'Change' : undefined,
  };
}

function findColumn(columns: DataColumn[], name: string): DataColumn | undefined {
  return columns.find(col => col.name === name);
}

function getDescriptionColumn(columns: DataColumn[], column: DataColumn, name: string): DataColumn {
  if (column.type === ColumnType.Identifier) {
    return column;
  }
  return columns.find(col => col.relatedTo === name && col.relationship === ColumnRelationship.Description) ?? column;
}

function formatSharedBrandsForSegmentation(
  params: Params,
  xColumnName: string,
  xData: { data: DataRows; timeFrame: TimeFrame },
  segmentName: string,
  segmentData: { data: DataRows; timeFrame: TimeFrame },
  sharedBrands: string[]
) {
  if (params?.grouping?.includes('brand_name')) {
    formatChartSharedBrands(xColumnName, xData, sharedBrands);
  }
  if (params?.sgSegment?.includes('brand_name')) {
    formatChartSharedBrands(segmentName, segmentData, sharedBrands);
  }
}

function formatSegmentDataSets(
  segmentDataSets: SegmentDataSet[],
  summaryDataSet: SegmentDataSet,
  options: {
    yColumn: DataColumn;
    additionalYColumn: DataColumn;
    currency: string;
    includeTotals: boolean;
  }
) {
  const { yColumn, additionalYColumn, currency, includeTotals } = options;

  formatDataSets(
    segmentDataSets.flatMap(s => s.dataSets),
    { yColumn, currency }
  );

  formatDataSets(summaryDataSet.dataSets, {
    yColumn,
    currency,
  });

  if (includeTotals) {
    formatDataSets(
      segmentDataSets.map(s => s.totals),
      {
        yColumn,
        currency,
      }
    );
    formatDataSets([summaryDataSet.totals], {
      yColumn,
      currency,
    });
    if (additionalYColumn) {
      formatDataSets(
        segmentDataSets.map(s => s.additionalTotals),
        {
          yColumn: additionalYColumn,
          currency,
        }
      );
      formatDataSets([summaryDataSet.additionalTotals], {
        yColumn: additionalYColumn,
        currency,
      });
    }
  }
}

function getRowLabel(row: DataRow, column: DataColumn): string {
  if (column.type === ColumnType.Identifier) {
    return labelToString(getIdentifiers(column as IdentifierColumn, row).name);
  }
  return labelToString(String(row.values[column.name]));
}

export function getDataPeriodCount(chartData: SegmentedChartData): number {
  return chartData.dataLabels.length;
}

export function labelToString(label: string | string[]): string {
  return Array.isArray(label) ? label.join(' ').split('|')[0] : label.split('|')[0];
}

function checkSetHasChanges(set: DataSet): boolean {
  return set.changes !== undefined;
}

export function checkChartDataHasChanges(chartData: SegmentedChartData): boolean {
  return chartData.segmentDataSets.some(segmentDataSet => segmentDataSet.dataSets.some(set => checkSetHasChanges(set)));
}
